/**
 * Recording Activity Store (Zustand)
 *
 * Tracks whether any voice recording is currently in flight, app-wide. A
 * counter (not a boolean) so overlapping recorders — e.g. the mobile Quick
 * Capture takeover and a NoteDetail "Voice to Text" session — can't clobber
 * each other's state.
 *
 * The sole consumer is `<IdleTimeoutWarning>`, which suppresses the idle
 * auto-logout while a recording is active so a long meeting recording isn't
 * signed out from under the user mid-capture. Recording is active user
 * presence, so this does not defeat the unattended-session intent of the
 * HIPAA/GDPR auto-logoff.
 */

import { useEffect } from 'react';
import { create } from 'zustand';

interface RecordingActivityState {
  activeCount: number;
  beginRecording: () => void;
  endRecording: () => void;
}

export const useRecordingActivityStore = create<RecordingActivityState>((set) => ({
  activeCount: 0,
  beginRecording: () => set((s) => ({ activeCount: s.activeCount + 1 })),
  endRecording: () => set((s) => ({ activeCount: Math.max(0, s.activeCount - 1) })),
}));

/** True while at least one voice recording is in flight anywhere in the app. */
export const useIsRecordingActive = (): boolean =>
  useRecordingActivityStore((s) => s.activeCount > 0);

/**
 * Registers an active recording for as long as `active` is true (and until the
 * calling component unmounts). Call from any recorder so the idle auto-logout
 * knows to stand down. Handles start, stop, cancel, auto-stop, and unmount
 * uniformly via the effect's cleanup.
 */
export function useRecordingActivityGuard(active: boolean): void {
  const beginRecording = useRecordingActivityStore((s) => s.beginRecording);
  const endRecording = useRecordingActivityStore((s) => s.endRecording);

  useEffect(() => {
    if (!active) return;
    beginRecording();
    return endRecording;
  }, [active, beginRecording, endRecording]);
}
